import { create } from 'zustand';
import { getMyProfile } from '../../profile/services/profileService';
import { useAuthStore } from './authStore';

export const useUserStore = create((set) => ({
  profile: null,
  // Bumped on every successful profile/billing save. Screens that render firm data served by
  // their own API (e.g. the dashboard company card) watch this to re-fetch instead of waiting
  // for a manual page refresh.
  profileUpdatedAt: null,
  selectedCountry: null, // Store selected country object
  selectedRegion: null, // Store selected region object
  regions: [], // Store regions for current country
  roles: [], // Store user's assigned roles
  permissions: [], // Store user permissions
  availableRoles: [], // Store all available roles from database

  setProfile: (profile) => {
    set({ profile });
  },

  // Re-fetches the signed-in user's own profile and stores it. Lets callers outside the profile
  // feature (e.g. Team page, after an admin edits their own row) pick up fresh profile data
  // without importing profileService directly — keeps that dependency inside features/auth,
  // which already owns the profile bootstrap call in useTenantBootstrap.js.
  refreshProfile: async () => {
    const profileResponse = await getMyProfile();
    set({ profile: profileResponse?.data ?? profileResponse });
  },

  clearProfile: () => {
    set({ profile: null, selectedCountry: null, selectedRegion: null, regions: [], roles: [], permissions: [], availableRoles: [] });
  },

  updateProfile: (updatedProfile) => {
    set((state) => ({
      profile: { ...state.profile, ...updatedProfile },
      profileUpdatedAt: Date.now()
    }));
  },

  setSelectedCountry: (country) => {
    set({ selectedCountry: country });
  },

  setSelectedRegion: (region) => {
    set({ selectedRegion: region });
  },

  setRegions: (regions) => {
    set({ regions });
  },

  setRolesAndPermissions: (roles, permissions) => {
    set({ roles, permissions });
  },

  setAvailableRoles: (availableRoles) => {
    set({ availableRoles });
  },

  // Get stored regions
  getRegions: () => {
    const state = useUserStore.getState();
    return state.regions;
  },

  // Get stored roles
  getRoles: () => {
    const state = useUserStore.getState();
    return state.roles;
  },

  // Get stored permissions
  getPermissions: () => {
    const state = useUserStore.getState();
    return state.permissions;
  },

  // Check if user has a specific role
  hasRole: (role) => {
    const state = useUserStore.getState();
    return state.roles.includes(role);
  },

  // Check if user has a specific permission
  hasPermission: (permission) => {
    const state = useUserStore.getState();
    return state.permissions.includes(permission);
  },

  // Get all available roles from database
  getAvailableRoles: () => {
    const state = useUserStore.getState();
    return state.availableRoles;
  },

  // Helper functions for ID-based lookups
  getCountryById: (countryId) => {
    const state = useUserStore.getState();
    return state.selectedCountry?.id.toString() === countryId.toString() ? state.selectedCountry : null;
  },

  getRegionById: (regionId) => {
    const state = useUserStore.getState();
    return state.selectedRegion?.id.toString() === regionId.toString() ? state.selectedRegion : null;
  },

  // Helper functions for client and site access
  getClientId: () => {
    const state = useUserStore.getState();
    return state.profile?.firm?.haloPsaClientId || null;
  },

  // The ACTIVE site. The profile's `firm.haloPsaSiteId` is only the customer's main site, which is
  // wrong once the user works on another site, so the selected site wins and the profile's is the
  // fallback for before a selection exists.
  getSiteId: () => {
    const state = useUserStore.getState();
    return (
      useAuthStore.getState().selectedSite?.haloPsaSiteId ||
      state.profile?.firm?.haloPsaSiteId ||
      null
    );
  },

  // Get both client and site IDs for API calls
  getClientAndSiteIds: () => {
    const state = useUserStore.getState();
    return {
      clientId: state.profile?.firm?.haloPsaClientId || null,
      siteId: state.getSiteId(),
    };
  },
}));
